import { createRoute, redirect, type AnyRoute } from '@tanstack/react-router';
import { AnsPage } from '@/features/relatorios/AnsPage';
import { BacklogPage } from '@/features/relatorios/BacklogPage';
import { FcrPage } from '@/features/relatorios/FcrPage';
import { CAMINHOS, RAIZ_DE_RELATORIOS } from '@/features/relatorios/navegacao';
import { ReaberturaPage } from '@/features/relatorios/ReaberturaPage';
import { RelatoriosLayout } from '@/features/relatorios/RelatoriosLayout';
import { ResolucaoPage } from '@/features/relatorios/ResolucaoPage';
import { VolumePage } from '@/features/relatorios/VolumePage';

/**
 * Rotas dos relatórios.
 *
 * Exportadas como **fábrica**, e não como objetos prontos, pelo mesmo motivo de
 * `features/admin/routes.ts` e `features/tickets/routes.ts`: `createRoute`
 * exige a rota pai, e o pai (`appRoute`, com o `AppShell` e a guarda de sessão)
 * mora em `app/router.tsx`, que pertence a outro dono. Receber o pai por
 * parâmetro deixa a integração ser uma linha lá, sem que esta feature importe
 * de dentro de `app/` — o que fecharia um ciclo entre o roteador e a tela que
 * ele monta.
 *
 * Volta **uma rota só**, com as abas já penduradas: quantos relatórios existem
 * e em que ordem aparecem é decisão da feature, e listá-los lá obrigaria a
 * mexer em `app/router.tsx` a cada indicador novo.
 *
 * Integração esperada em `app/router.tsx` — substituindo o `relatoriosRoute`
 * de placeholder que existe hoje:
 *
 * ```ts
 * const relatoriosRoute = criarRotasDeRelatorios(appRoute);
 * ```
 *
 * O item `/relatorios` de `app/nav.ts` já aponta para cá e não muda.
 */
export function criarRotasDeRelatorios<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const raiz = createRoute({
    getParentRoute: () => rotaPai,
    path: RAIZ_DE_RELATORIOS,
    component: RelatoriosLayout,
  });

  /**
   * `/relatorios` sozinho não é tela: redireciona para a primeira aba.
   *
   * Repetir o conteúdo de "Volume" no índice daria dois endereços para a mesma
   * tela, e o item da navegação principal acenderia sem que nenhuma aba
   * ficasse marcada.
   */
  const indice = createRoute({
    getParentRoute: () => raiz,
    path: '/',
    // `href` e não `to`: este módulo é importado por `app/router.tsx`, então os
    // caminhos daqui ainda não existem no tipo do roteador quando ele é
    // compilado. Mesma razão de `features/admin/routes.ts`.
    beforeLoad: () => {
      throw redirect({ href: `${RAIZ_DE_RELATORIOS}/${CAMINHOS.volume}` });
    },
  });

  const aba = (caminho: string, component: () => JSX.Element) =>
    createRoute({ getParentRoute: () => raiz, path: caminho, component });

  return raiz.addChildren([
    indice,
    aba(CAMINHOS.volume, VolumePage),
    aba(CAMINHOS.ans, AnsPage),
    aba(CAMINHOS.resolucao, ResolucaoPage),
    aba(CAMINHOS.backlog, BacklogPage),
    aba(CAMINHOS.reabertura, ReaberturaPage),
    aba(CAMINHOS.fcr, FcrPage),
  ]);
}

export { CAMINHOS, RAIZ_DE_RELATORIOS };
